import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileQuestion, Code2 } from 'lucide-react';

import { getDocumentTypeBySlug, SchemaQueryKeys, type FieldDefinitionResponse } from '../../../schema/api';

import { ApiError } from '../../../../api/client';
import { useAuth } from '../../../../auth/useAuth';
import { JsonViewerModal, type JsonViewerTab } from '../../../../components/JsonViewerModal';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { Skeleton } from '../../../../components/Skeleton';
import { useToastStore } from '../../../../stores/toastStore';
import {
  CandidateChangeKinds,
  CandidateFieldStatuses,
  DiscoveryQueryKeys,
  DiscoveryStatuses,
  getDiscoveryRawAiResponse,
  type CandidateFieldResponse,
  type ModifyCandidateFieldRequest,
} from '../api';
import { CandidateFieldCard } from '../components/CandidateFieldCard';
import {
  useAcceptField,
  useDraft,
  useModifyField,
  useRejectField,
} from '../hooks';

/**
 * Step 3 — review every candidate field proposed by the engine. The user can
 * accept, reject or edit individual fields; the footer "Continuar" call is
 * enabled once at least one field has been accepted (or modified).
 */
export function DiscoveryReviewStep(): JSX.Element {
  const { draftId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();
  const pushToast = useToastStore((s) => s.push);
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useDraft(draftId);
  const accept = useAcceptField();
  const reject = useRejectField();
  const modify = useModifyField();
  const { hasScope } = useAuth();
  const canSeeRawJson = hasScope('platform:debug');
  const [jsonModalOpen, setJsonModalOpen] = useState<boolean>(false);

  // Lazy-fetch the raw AI JSON only when the modal is open AND the user has the debug scope.
  // A 403 means the backend disagrees with the JWT's scopes — we hide the tab via errorMessage.
  const rawAiQuery = useQuery({
    queryKey: draftId !== undefined ? DiscoveryQueryKeys.rawAi(draftId) : ['discovery', 'raw-ai', 'noop'],
    queryFn: ({ signal }) => getDiscoveryRawAiResponse(draftId as string, signal),
    enabled: jsonModalOpen && canSeeRawJson && draftId !== undefined,
    retry: (failureCount, err) => {
      // Don't retry on 403 — the user just doesn't have the scope on this draft/tenant.
      if (err instanceof ApiError && err.detail.kind === 'forbidden') {
        return false;
      }
      return failureCount < 2;
    },
    staleTime: 60_000,
  });
  // For re-education drafts: fetch the source type head to drive the before→after diff.
  // Silently omits the diff if the fetch fails — badges still show.
  const sourceSlug = data?.sourceDocumentTypeSlug ?? null;
  const sourceTypeQuery = useQuery({
    queryKey: sourceSlug !== null
      ? SchemaQueryKeys.detail(sourceSlug)
      : ['schema', 'detail', '__skip__'],
    queryFn: ({ signal }) => getDocumentTypeBySlug(sourceSlug as string, { signal }),
    enabled: sourceSlug !== null,
    staleTime: 60_000,
  });
  // Build a lookup map for the source schema by field name (case-insensitive).
  const sourceFieldsByName = useMemo<Map<string, FieldDefinitionResponse>>(() => {
    const fields = sourceTypeQuery.data?.fields ?? [];
    return new Map(fields.map((f) => [f.name.toLowerCase(), f]));
  }, [sourceTypeQuery.data]);

  // Narrow race window: status moves to ProposalReady but the cache snapshot
  // arrived without fields (cf. Phase A bug). Show a guarded loading state for
  // up to 5 s before falling back to the canonical empty state.
  const [hasGivenUp, setHasGivenUp] = useState<boolean>(false);
  useEffect(() => {
    setHasGivenUp(false);
    const handle = setTimeout(() => setHasGivenUp(true), 5000);
    return () => clearTimeout(handle);
  }, [draftId]);

  // Memoised so its reference is stable across renders — otherwise the `?? []` fallback makes a
  // fresh array every render and churns the useMemo below (react-hooks/exhaustive-deps).
  const rawCandidateFields = useMemo(() => data?.candidateFields ?? [], [data]);

  // For sourced (re-education) drafts: order changed fields first (Modified/New/RemovalSuggested),
  // then Unchanged — stable sort within each group so server order is preserved.
  const candidateFields = useMemo<CandidateFieldResponse[]>(() => {
    if (sourceSlug === null) {
      return rawCandidateFields;
    }
    const changed = rawCandidateFields.filter(
      (f) =>
        f.changeKind === CandidateChangeKinds.Modified ||
        f.changeKind === CandidateChangeKinds.New ||
        f.changeKind === CandidateChangeKinds.RemovalSuggested,
    );
    const unchanged = rawCandidateFields.filter(
      (f) =>
        f.changeKind == null ||
        f.changeKind === CandidateChangeKinds.Unchanged,
    );
    return [...changed, ...unchanged];
  }, [rawCandidateFields, sourceSlug]);
  const acceptedCount = useMemo(
    () =>
      candidateFields.filter(
        (f) =>
          f.status === CandidateFieldStatuses.Accepted ||
          f.status === CandidateFieldStatuses.Modified,
      ).length,
    [candidateFields],
  );
  const busy = accept.isPending || reject.isPending || modify.isPending;

  const surfaceError = (err: unknown, title: string): void => {
    const message = err instanceof ApiError ? err.message : 'Error desconocido.';
    pushToast({ tone: 'error', title, description: message });
  };

  const handleAccept = (fieldId: string): void => {
    if (draftId === undefined) {
      return;
    }
    accept.mutate(
      { draftId, fieldId },
      { onError: (err) => surfaceError(err, 'No se pudo aceptar el campo') },
    );
  };

  const handleReject = (fieldId: string): void => {
    if (draftId === undefined) {
      return;
    }
    reject.mutate(
      { draftId, fieldId },
      { onError: (err) => surfaceError(err, 'No se pudo rechazar el campo') },
    );
  };

  const handleModify = (fieldId: string, request: ModifyCandidateFieldRequest): void => {
    if (draftId === undefined) {
      return;
    }
    modify.mutate(
      { draftId, fieldId, request },
      { onError: (err) => surfaceError(err, 'No se pudo modificar el campo') },
    );
  };

  // Why sequential: each mutation loads the draft, mutates it, and upserts back. Firing them in
  // parallel races against the same Cosmos document — only one upsert wins and the others either
  // overwrite or fail. Awaiting mutateAsync in a loop serialises the writes so every accepted
  // field lands in the persisted draft.
  const handleBulkAccept = async (): Promise<void> => {
    if (draftId === undefined) {
      return;
    }
    const proposed = candidateFields.filter((f) => f.status === CandidateFieldStatuses.Proposed);
    for (const field of proposed) {
      try {
        await accept.mutateAsync({ draftId, fieldId: field.id });
      } catch (err) {
        surfaceError(err, `No se pudo aceptar "${field.functionalName}"`);
        return;
      }
    }
  };

  const handleBulkReject = async (): Promise<void> => {
    if (draftId === undefined) {
      return;
    }
    const proposed = candidateFields.filter((f) => f.status === CandidateFieldStatuses.Proposed);
    for (const field of proposed) {
      try {
        await reject.mutateAsync({ draftId, fieldId: field.id });
      } catch (err) {
        surfaceError(err, `No se pudo rechazar "${field.functionalName}"`);
        return;
      }
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="discovery-review-skeleton">
        {[0, 1, 2].map((i) => (
          <div key={i} className="card flex flex-col gap-3 p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-5 w-14" />
              <Skeleton className="h-5 w-24 rounded-full" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        ))}
      </div>
    );
  }
  if (isError || data === undefined) {
    const message = error instanceof Error ? error.message : 'Error desconocido.';
    return (
      <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        <strong className="font-semibold">No se pudo cargar la propuesta de tipo.</strong>
        <p className="mt-1">{message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-24">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Revisar campos propuestos
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {candidateFields.length} campos sugeridos · {acceptedCount} aceptados.
          </p>
          {sourceSlug !== null ? (
            <p className="mt-1 text-sm text-indigo-700">
              Reeducación de{' '}
              <code className="font-mono text-xs">{sourceSlug}</code> — al publicar
              se creará una nueva versión de ese tipo (versionado automático, con rollback disponible).
            </p>
          ) : null}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-secondary inline-flex items-center gap-1"
            onClick={() => setJsonModalOpen(true)}
            disabled={busy}
          >
            <Code2 className="h-4 w-4" aria-hidden strokeWidth={2} />
            Ver JSON
          </button>
          <button type="button" className="btn-secondary" onClick={handleBulkAccept} disabled={busy}>
            Aceptar todos
          </button>
          <button type="button" className="btn-secondary" onClick={handleBulkReject} disabled={busy}>
            Rechazar todos
          </button>
        </div>
      </header>

      {candidateFields.length === 0 ? (
        data.status === DiscoveryStatuses.ProposalReady && !hasGivenUp ? (
          <div
            className="card flex flex-col items-center gap-3 p-8 text-sm text-slate-500"
            data-testid="discovery-review-loading-guard"
          >
            <LoadingSpinner />
            <p>Cargando propuesta…</p>
          </div>
        ) : (
          <div
            className="card flex flex-col items-center gap-4 p-10 text-center text-sm text-slate-500"
            data-testid="discovery-review-empty"
          >
            <FileQuestion className="h-12 w-12 text-slate-300" strokeWidth={1.5} aria-hidden />
            <div>
              <h2 className="text-base font-semibold text-slate-800">Aún no hay propuesta</h2>
              <p className="mt-1 text-sm text-slate-500">
                El motor todavía no ha sugerido campos para esta propuesta. Puedes volver
                al paso de subida y añadir más muestras, o reintentar la consulta.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  if (draftId !== undefined) {
                    navigate(`/document-types/discover/${encodeURIComponent(draftId)}/upload`);
                  }
                }}
              >
                Volver y añadir muestras
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  if (draftId !== undefined) {
                    setHasGivenUp(false);
                    void queryClient.invalidateQueries({
                      queryKey: DiscoveryQueryKeys.draft(draftId),
                    });
                  }
                }}
              >
                Reintentar
              </button>
            </div>
          </div>
        )
      ) : (
        <div className="space-y-3">
          {candidateFields.map((field) => (
            <CandidateFieldCard
              key={field.id}
              field={field}
              busy={busy}
              onAccept={handleAccept}
              onReject={handleReject}
              onModify={handleModify}
              sourceField={
                field.changeKind === CandidateChangeKinds.Modified
                  ? (sourceFieldsByName.get(field.functionalName.toLowerCase()) ?? null)
                  : null
              }
            />
          ))}
        </div>
      )}

      <JsonViewerModal
        open={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        title="JSON de la propuesta"
        subtitle={`Descubrimiento ${data.id} · ${data.candidateFields.length} campos`}
        tabs={buildJsonTabs({
          draftData: data,
          canSeeRaw: canSeeRawJson,
          rawIsLoading: rawAiQuery.isLoading,
          rawError: rawAiQuery.error,
          rawValue: rawAiQuery.data?.rawAiJson ?? null,
        })}
      />

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <p className="text-sm text-slate-500">
            {acceptedCount > 0
              ? `Se incluirán ${acceptedCount} campo(s) en la madurez.`
              : 'Acepta al menos un campo para continuar.'}
          </p>
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              if (draftId !== undefined) {
                navigate(`/document-types/discover/${encodeURIComponent(draftId)}/maturity`);
              }
            }}
            disabled={acceptedCount === 0 || busy}
          >
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}

interface BuildJsonTabsArgs {
  draftData: unknown;
  canSeeRaw: boolean;
  rawIsLoading: boolean;
  rawError: unknown;
  rawValue: string | null;
}

function buildJsonTabs({
  draftData,
  canSeeRaw,
  rawIsLoading,
  rawError,
  rawValue,
}: BuildJsonTabsArgs): JsonViewerTab[] {
  const tabs: JsonViewerTab[] = [
    {
      id: 'normalized',
      label: 'JSON',
      value: draftData,
    },
  ];
  if (!canSeeRaw) {
    return tabs;
  }
  const isForbidden = rawError instanceof ApiError && rawError.detail.kind === 'forbidden';
  if (isForbidden) {
    // Tab is gated server-side too — surface as a one-liner instead of removing the tab,
    // so the user understands why this account does not see the raw payload.
    tabs.push({
      id: 'raw',
      label: 'JSON raw',
      value: null,
      errorMessage: 'Esta cuenta no tiene el scope platform:debug.',
    });
    return tabs;
  }
  const errMessage =
    rawError instanceof ApiError
      ? rawError.message
      : rawError instanceof Error
        ? rawError.message
        : rawError !== null && rawError !== undefined
          ? 'Error desconocido al obtener la respuesta cruda.'
          : undefined;
  tabs.push({
    id: 'raw',
    label: 'JSON raw',
    isLoading: rawIsLoading,
    ...(errMessage !== undefined ? { errorMessage: errMessage } : {}),
    value: rawValue ?? '(La propuesta no tiene respuesta cruda persistida todavía.)',
  });
  return tabs;
}
